import { supabase } from '@/integrations/supabase/client';

const BUCKET = 'agency-logos';
const SIGN_TTL_SECONDS = 60 * 60 * 24 * 7; // 7 days — refreshed on demand, never stored

type Cached = { url: string; expiresAt: number };
const cache = new Map<string, Cached>();
const inflight = new Map<string, Promise<string | null>>();

/**
 * Extract the object path (inside the agency-logos bucket) from any of the
 * URL shapes we've stored historically:
 *   - https://<ref>.supabase.co/storage/v1/object/public/agency-logos/<path>
 *   - https://<ref>.supabase.co/storage/v1/object/sign/agency-logos/<path>?token=...
 *   - https://<ref>.supabase.co/storage/v1/object/authenticated/agency-logos/<path>
 *   - agency-logos/<path>
 *   - <path> (bare object key)
 */
export function extractAgencyLogoPath(input: string | null | undefined): string | null {
  if (!input) return null;
  const s = input.trim();
  if (!s) return null;
  // Full Supabase storage URL
  const m = s.match(/\/storage\/v1\/object\/(?:public|sign|authenticated)\/agency-logos\/([^?#]+)/);
  if (m) return decodeURIComponent(m[1]);
  // Prefixed form
  if (s.startsWith(`${BUCKET}/`)) return s.slice(BUCKET.length + 1);
  // Absolute URL to some other host — can't sign, return null so caller uses raw
  if (/^https?:\/\//i.test(s)) return null;
  return s;
}

/** True if this URL points to our storage bucket (and therefore is signable). */
export function isAgencyLogoStorageUrl(input: string | null | undefined): boolean {
  return !!extractAgencyLogoPath(input);
}

/**
 * Resolve a stored logo_url into a currently-valid signed URL.
 * - Never returns stale/expired signed URLs (in-memory TTL with 5 min safety margin).
 * - Returns the original string when it isn't a storage-hosted asset.
 * - Returns null on failure so the UI can fall back to initials.
 */
export async function resolveAgencyLogoUrl(
  input: string | null | undefined,
  opts: { forceRefresh?: boolean } = {},
): Promise<string | null> {
  if (!input) return null;
  const path = extractAgencyLogoPath(input);
  if (!path) return input; // external URL — pass through
  const now = Date.now();
  if (opts.forceRefresh) {
    cache.delete(path);
    inflight.delete(path);
  }
  const hit = cache.get(path);
  if (hit && hit.expiresAt - now > 5 * 60_000) return hit.url;
  if (inflight.has(path)) return inflight.get(path)!;
  const p = (async () => {
    try {
      const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, SIGN_TTL_SECONDS);
      if (error || !data?.signedUrl) return null;
      cache.set(path, { url: data.signedUrl, expiresAt: now + SIGN_TTL_SECONDS * 1000 });
      return data.signedUrl;
    } catch {
      return null;
    } finally {
      inflight.delete(path);
    }
  })();
  inflight.set(path, p);
  return p;
}

/** Purge the in-memory signed-URL cache for a specific stored logo (or all). */
export function invalidateAgencyLogoCache(input?: string | null) {
  if (!input) { cache.clear(); inflight.clear(); return; }
  const path = extractAgencyLogoPath(input);
  if (path) { cache.delete(path); inflight.delete(path); }
}

/** Compute up-to-3 char initials for an avatar fallback. */
export function agencyInitials(name?: string | null): string {
  if (!name) return 'A';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Health probe for the audit page — HEAD the resolved URL. */
export async function probeAgencyLogo(rawUrl: string | null | undefined): Promise<{
  hasUrl: boolean;
  path: string | null;
  resolved: string | null;
  reachable: boolean;
  storageExists: boolean | null;
  error?: string;
}> {
  if (!rawUrl) return { hasUrl: false, path: null, resolved: null, reachable: false, storageExists: null };
  const path = extractAgencyLogoPath(rawUrl);
  let storageExists: boolean | null = null;
  if (path) {
    try {
      const parent = path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '';
      const file = path.slice(parent.length + (parent ? 1 : 0));
      const { data } = await supabase.storage.from(BUCKET).list(parent, { search: file, limit: 1 });
      storageExists = !!data?.some((o) => o.name === file);
    } catch {
      storageExists = null;
    }
  }
  const resolved = await resolveAgencyLogoUrl(rawUrl);
  let reachable = false;
  if (resolved) {
    try {
      const r = await fetch(resolved, { method: 'HEAD' });
      reachable = r.ok;
    } catch {
      reachable = false;
    }
  }
  return { hasUrl: true, path, resolved, reachable, storageExists };
}
